import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import AuthBackground from '../components/auth/AuthBackground';
import AuthNavbar from '../components/navigation/auth';

import { useAuth } from '../contexts/AuthContext';
import { STORAGE_KEYS } from '../constants/storage';

/**
 * Register Page
 * 
 * User registration page with form fields and social signup options.
 * 
 * Exact styles from auth.css:
 * - Card max width: 420px
 * - Card padding: 32px 32px 28px
 * - Card border radius: 16px
 * - Background: rgba(255, 255, 255, 0.05) with backdrop blur 20px
 * - Shadow: 0 20px 45px rgba(0, 0, 0, 0.3), 0 0 30px rgba(255, 255, 255, 0.05)
 * - Border: 1px solid rgba(255, 255, 255, 0.1)
 */
const Register = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signUp, error: authError, loading: authLoading } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    country: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
  });

  useEffect(() => {
    setIsVisible(false);
    const timer = setTimeout(() => setIsVisible(true), 80);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Clear errors when form data changes
  useEffect(() => {
    setLocalError(null);
  }, [formData.email, formData.password, formData.confirmPassword, formData.fullName, formData.country]);

  // Display auth errors
  useEffect(() => {
    if (authError) {
      setLocalError(authError);
    }
  }, [authError]);

  /**
   * Validates the registration form
   * @returns {string | null} Error message or null if valid
   */
  const validateForm = () => {
    if (!formData.fullName.trim()) {
      return "Full name is required";
    }
    if (!formData.email.trim()) {
      return "Email is required";
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return "Invalid email address";
    }
    if (!formData.country) {
      return "Please select your country";
    }
    if (!formData.password) {
      return "Password is required";
    }
    if (formData.password.length < 6) {
      return "Password should be at least 6 characters";
    }
    if (formData.password !== formData.confirmPassword) {
      return "Passwords do not match";
    }
    if (!formData.agreeToTerms) {
      return "You must agree to the Terms & Conditions";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError(null);

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      await signUp(formData.email, formData.password, formData.fullName, formData.country);

      // Store additional user data in localStorage (non-critical, for UI purposes)
      localStorage.setItem(STORAGE_KEYS.USER_EMAIL, formData.email);
      localStorage.setItem(STORAGE_KEYS.USER_FULLNAME, formData.fullName);

      // Redirect to dashboard after successful registration
      navigate('/dashboard');
    } catch (error) {
      // Error is already handled by AuthContext and displayed via authError
      // Just ensure we don't navigate on error
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAccount = async () => {
    setLocalError(null);

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      await signUp(formData.email, formData.password, formData.fullName, formData.country);

      // Store additional user data in localStorage (non-critical, for UI purposes)
      if (formData.email) {
        localStorage.setItem(STORAGE_KEYS.USER_EMAIL, formData.email);
      }
      if (formData.fullName) {
        localStorage.setItem(STORAGE_KEYS.USER_FULLNAME, formData.fullName);
      }

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      // Error is already handled by AuthContext
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <div
      className="auth-page min-h-screen bg-main-gradient relative overflow-x-hidden w-full"
      style={{
        height: '100vh',
        overflowY: 'auto',
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%'
      }}
    >
      {/* Glowing rectangle background */}
      <AuthBackground />

      {/* Auth navbar */}
      <AuthNavbar />

      {/* Main auth panel */}
      <div className="auth-main bg-transparent relative z-2 pt-16 min-h-[calc(100vh-64px)]">
        <div
          className="auth-panel flex-1 flex items-center justify-center py-4 md:py-8 px-3 md:px-6 pb-6 md:pb-10 min-h-full"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateX(0) scale(1)' : 'translateX(25px) scale(0.97)',
            transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1), transform 500ms cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: isVisible ? 'auto' : 'transform, opacity',
          }}
        >
          {/* Auth card - MOBILE: Full width */}
          <div className="auth-card w-full md:max-w-auth-card glass-bg-auth rounded-auth-card py-6 md:py-8 px-6 md:px-8 pb-5 md:pb-7 shadow-auth-card border border-white/10 relative z-2 transition-all duration-300 hover:shadow-auth-card-hover">
            {/* Title */}
            <h1 className="auth-title text-xl md:text-auth-title font-bold mb-1 text-white">
              Create Account
            </h1>

            {/* Subtitle */}
            <p className="auth-subtitle text-xs md:text-auth-subtitle text-[#a0a0a0] mb-4 md:mb-6">
              Sign up to start using <span className="lawnance-brand">LAWNANCE</span>
            </p>

            {/* Error Message */}
            {localError && (
              <div className="mb-3 md:mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 backdrop-blur-sm">
                <p className="text-xs md:text-sm text-red-400">{localError}</p>
              </div>
            )}

            {/* Register Form */}
            <form className="auth-form flex flex-col gap-3 md:gap-[14px]" onSubmit={handleSubmit}>
              {/* Full Name Field */}
              <label className="auth-label flex flex-col gap-1.5 text-auth-label text-auth-label opacity-0 animate-fade-in-up-smooth [animation-delay:0.1s]">
                Full Name
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="auth-input"
                  required
                />
              </label>

              {/* Email Field */}
              <label className="auth-label flex flex-col gap-1.5 text-auth-label text-auth-label opacity-0 animate-fade-in-up-smooth [animation-delay:0.15s]">
                Email
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="auth-input"
                  required
                />
              </label>

              {/* Country Field */}
              <label className="auth-label flex flex-col gap-1.5 text-auth-label text-auth-label opacity-0 animate-fade-in-up-smooth [animation-delay:0.2s]">
                Country
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="auth-input"
                  required
                >
                  <option value="">Select your country</option>
                  <option value="US">United States</option>
                  <option value="GB">United Kingdom</option>
                  <option value="CA">Canada</option>
                  <option value="AU">Australia</option>
                  <option value="DE">Germany</option>
                  <option value="FR">France</option>
                  <option value="IT">Italy</option>
                  <option value="ES">Spain</option>
                  <option value="NL">Netherlands</option>
                  <option value="BE">Belgium</option>
                  <option value="CH">Switzerland</option>
                  <option value="AT">Austria</option>
                  <option value="SE">Sweden</option>
                  <option value="NO">Norway</option>
                  <option value="DK">Denmark</option>
                  <option value="FI">Finland</option>
                  <option value="IE">Ireland</option>
                  <option value="PL">Poland</option>
                  <option value="CZ">Czech Republic</option>
                  <option value="PT">Portugal</option>
                  <option value="GR">Greece</option>
                  <option value="TR">Turkey</option>
                  <option value="RU">Russia</option>
                  <option value="JP">Japan</option>
                  <option value="CN">China</option>
                  <option value="KR">South Korea</option>
                  <option value="IN">India</option>
                  <option value="SG">Singapore</option>
                  <option value="MY">Malaysia</option>
                  <option value="TH">Thailand</option>
                  <option value="ID">Indonesia</option>
                  <option value="PH">Philippines</option>
                  <option value="VN">Vietnam</option>
                  <option value="NZ">New Zealand</option>
                  <option value="BR">Brazil</option>
                  <option value="MX">Mexico</option>
                  <option value="AR">Argentina</option>
                  <option value="CL">Chile</option>
                  <option value="CO">Colombia</option>
                  <option value="PE">Peru</option>
                  <option value="ZA">South Africa</option>
                  <option value="EG">Egypt</option>
                  <option value="NG">Nigeria</option>
                  <option value="KE">Kenya</option>
                  <option value="AE">United Arab Emirates</option>
                  <option value="SA">Saudi Arabia</option>
                  <option value="IL">Israel</option>
                  <option value="QA">Qatar</option>
                  <option value="KW">Kuwait</option>
                  <option value="OTHER">Other</option>
                </select>
              </label>

              {/* Password Field */}
              <label className="auth-label flex flex-col gap-1.5 text-auth-label text-auth-label opacity-0 animate-fade-in-up-smooth [animation-delay:0.25s]">
                Password
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="auth-input"
                  required
                />
              </label>

              {/* Confirm Password Field */}
              <label className="auth-label flex flex-col gap-1.5 text-auth-label text-auth-label opacity-0 animate-fade-in-up-smooth [animation-delay:0.3s]">
                Confirm Password
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="auth-input"
                  required
                />
              </label>

              {/* Terms checkbox */}
              <label className="auth-checkbox auth-checkbox-margin flex items-center gap-1.5 text-[12px] text-auth-label transition-colors duration-300 cursor-pointer hover:text-white mt-1.5">
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  className="cursor-pointer transition-all duration-300"
                  required
                />
                I agree to the{' '}
                <a
                  href="#terms"
                  className="text-white no-underline transition-opacity duration-200 hover:opacity-80 hover:underline"
                >
                  Terms & Conditions
                </a>
              </label>

              {/* Submit Button */}
              <button
                type="submit"
                onClick={handleCreateAccount}
                disabled={isSubmitting || authLoading}
                className="auth-primary-btn mt-1 rounded-full border-none px-4 py-2.5 text-auth-primary-btn font-medium bg-white/15 text-white cursor-pointer transition-all duration-300 border border-white/20 hover:bg-white/25 hover:-translate-y-0.5 hover:shadow-auth-primary-btn-hover hover:border-white/30 active:scale-[0.98] backdrop-blur-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {isSubmitting || authLoading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>

            {/* Divider */}
            <div className="auth-divider flex items-center gap-2 text-[11px] text-[#6b7280] my-4 mt-4 mb-2">
              <div className="flex-1 h-px bg-white/15" />
              OR
              <div className="flex-1 h-px bg-white/15" />
            </div>

            {/* Social Signup Buttons */}
            <div className="auth-social-row flex gap-2 md:gap-2 mb-1">
              <button
                type="button"
                className="auth-social-btn auth-social-google flex-1 min-w-[120px] flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-full border border-white/15 bg-white/5 text-sm md:text-xs text-auth-label cursor-pointer transition-all duration-300 hover:bg-white/10 hover:border-white/25 hover:-translate-y-0.5 hover:shadow-auth-social-btn-hover hover:text-white active:scale-[0.98] min-h-[48px]"
              >
                <span className="auth-social-icon text-[13px] text-[#ea4335]">G</span>
                Google
              </button>
              <button
                type="button"
                className="auth-social-btn auth-social-microsoft flex-1 min-w-[120px] flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-full border border-white/15 bg-white/5 text-sm md:text-xs text-auth-label cursor-pointer transition-all duration-300 hover:bg-white/10 hover:border-white/25 hover:-translate-y-0.5 hover:shadow-auth-social-btn-hover hover:text-white active:scale-[0.98] min-h-[48px]"
              >
                <span className="auth-social-icon text-[13px] text-[#2563eb]">M</span>
                Microsoft
              </button>
            </div>

            {/* Footer text */}
            <p className="auth-footer-text mt-2.5 text-[12px] text-[#9ca3af] text-center">
              Already have an account?{' '}
              <Link
                to="/login"
                className="auth-link text-white no-underline transition-opacity duration-200 hover:opacity-80 hover:underline"
              >
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;